import dayjs from 'dayjs';
import { useContext, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { BankNames, DictTypes } from '@/constants';
import { keysToCamelCase, setDictionaryOptions } from '@/services';
import { formatPhoneNumber, formatPrice } from '@/services/formatter';
import { debounce } from '@/services/helpers/debounce';
import { isDictHasAll, safeKey } from '@/services/helpers/dict';
import { getBirthDateFromIIN } from '@/services/helpers/getBirthDateFromIIN';
import { useAppDispatch, useAppSelector } from '@/store';
import { setPageTitle } from '@/store/newOrder.slice';

import { CreateTabContext } from '../../ExistedOrder';
import { getAutoCreditDraftKey } from '../../helpers/helpers';
import {
  ADDITIONAL_PRODUCT_CODES,
  RADIO_PRODUCTS_FIELD_NAMES,
  defaultAutoCreditValues
} from '../constants';
import type {
  ClientContactsInterface,
  ClientInfoInterface,
  ClientJobInfoInterface,
  CreateAutoCreditProps,
  DocumentsInterface,
  ProductInfoInterface
} from '../types/form-types';
import type { StockBoundElementType, TAddProducts } from '../types/products';

const isBerekeKasko = (code: string): boolean => {
  return [
    ADDITIONAL_PRODUCT_CODES[BankNames.bereke].kasko_3_0,
    ADDITIONAL_PRODUCT_CODES[BankNames.bereke].kasko_1_8
  ].includes(code);
};

const useAutoCreditForm = () => {
  const dispatch = useAppDispatch();
  const { orderApp, leasingOrder } = useAppSelector((state) => state.newOrder);
  const { dicts, stockBoundsProductsDict, additionalProductsDict } =
    useAppSelector((state) => state.dict);

  const { isFieldsFrozen } = useContext(CreateTabContext);
  const methods = useForm<CreateAutoCreditProps>({
    mode: isFieldsFrozen ? 'onSubmit' : 'all',
    defaultValues: defaultAutoCreditValues,
    delayError: 200
  });
  const formData = useWatch({ control: methods.control });

  const marks = useMemo(
    () => setDictionaryOptions(DictTypes.Brands, dicts) || [],
    [dicts]
  );
  const models = useMemo(
    () => setDictionaryOptions(DictTypes.Models, dicts) || [],
    [dicts]
  );

  const saveDraftValuesToStorage = useMemo(
    () =>
      debounce((uuid: string, data: string) => {
        sessionStorage.setItem(getAutoCreditDraftKey(uuid), data);
      }, 500),
    []
  );

  // Сохранение данных в sessionStorage
  useEffect(() => {
    if (!orderApp) return;

    if (orderApp.status !== 'draft') {
      if (sessionStorage.getItem(getAutoCreditDraftKey(orderApp.uuid)))
        sessionStorage.removeItem(getAutoCreditDraftKey(orderApp.uuid));
      return;
    }

    saveDraftValuesToStorage(orderApp.uuid, JSON.stringify(formData));
  }, [formData, orderApp?.uuid, orderApp?.status]);

  // Заполнение формы
  useEffect(() => {
    dispatch(setPageTitle('Новая заявка'));
    if (!orderApp) return;

    const savedFormData = sessionStorage.getItem(
      getAutoCreditDraftKey(orderApp.uuid)
    );
    if (savedFormData) {
      const parsedSavedData = JSON.parse(savedFormData);
      if (!parsedSavedData.clientPhoto || !parsedSavedData.clientPhoto.base64) {
        parsedSavedData.clientPhoto =
          orderApp.additionalInfo.files?.clientPhoto ?? null;
      }
      if (!parsedSavedData.docPhoto || !parsedSavedData.docPhoto.base64) {
        parsedSavedData.docPhoto =
          orderApp.additionalInfo.files?.docPhoto ?? null;
      }
      methods.reset(parsedSavedData);
      return;
    }

    if (
      !isDictHasAll(dicts, [
        DictTypes.Equipments,
        DictTypes.Dealers,
        DictTypes.LeasingIndividualsSettings
      ])
    )
      return;

    const orderProduct = orderApp.productInfo.product;

    const markModelYear = orderProduct.markModelYear;
    const usedCar = orderProduct.usedCar;
    const usedCarInfo = usedCar
      ? {
          info: usedCar,
          title: usedCar.brand_name + ' ' + usedCar.model_name,
          vinCode: usedCar.vin_code,
          year: usedCar.year,
          color: usedCar.color,
          engineCapacity: usedCar.engine_capacity.toString(),
          enginePower: usedCar.engine_power.toString()
        }
      : undefined;

    const mark = marks.find((item) => item.value === markModelYear?.brand);
    const model = models.find((item) => item.value === markModelYear?.model);
    const equipment =
      dicts[DictTypes.Equipments].getBy.uuid[safeKey(markModelYear?.equipment)];

    const dealers = dicts[DictTypes.Dealers].content.filter(
      (item) => item.is_active
    );
    const dealer = dealers?.find(
      (item) =>
        item.uuid === orderProduct.chosenDealer ||
        item.code === Number(orderProduct.chosenDealer)
    );
    const isMultiBrandDealer =
      orderProduct.chosenDealer !== orderProduct.dealer;

    const isDealerHasLeasing = dicts[
      DictTypes.LeasingIndividualsSettings
    ].content.some(
      (item) => item.dealer_uuid === dealer?.uuid && item.is_active
    );

    const localDealerCode = localStorage.getItem('dealer');
    const productInfo: ProductInfoInterface = {
      dealer: String(dealer?.code || localDealerCode || ''),
      autoType: String(orderProduct.autoType || ''),
      sentTo: !isDealerHasLeasing ? 'banks' : orderApp.sentTo,
      markModelYear: markModelYear
        ? {
            brand: mark
              ? { name: mark.label, code: String(mark.value) }
              : undefined,
            model: model
              ? { name: model.label, code: String(model.value) }
              : undefined,
            equipment: equipment,
            year: mark ? String(markModelYear?.year || '') : '',
            dealer:
              isMultiBrandDealer && dealer
                ? {
                    name: dealer.name,
                    code: String(dealer.code)
                  }
                : undefined
          }
        : undefined,
      usedCar: usedCarInfo ? usedCarInfo : undefined,
      costCar: formatPrice(orderProduct.costCar),
      initialPayment: formatPrice(orderProduct.initFee),
      loanTerm: String(orderProduct.loanTerm || ''),
      engineType: String(orderProduct.engineType || ''),
      engineCapacity: '',
      paymentScheme: String(orderProduct.paymentScheme || ''),
      proofInCome: orderProduct.proofInCome
    };

    let stockBounds: StockBoundElementType[] = [];

    if (stockBoundsProductsDict && stockBoundsProductsDict.length > 0) {
      const isHasStockBonds =
        orderProduct.stocksBonds && orderApp.status !== 'draft';
      stockBounds = stockBoundsProductsDict
        .filter((item) => item.is_active)
        .map((elem) => {
          const isChecked =
            isHasStockBonds &&
            orderProduct.stocksBonds?.some((value) => value.code === elem.code);
          const res = {
            uuid: elem.uuid,
            code: elem.code,
            name: elem.name,
            checked: isChecked || elem.enabled_by_default,
            bankUuid: elem.bank,
            bank: elem.bank_name,
            description: elem.description,
            productConditions: keysToCamelCase(elem.Products_Info),
            productModels: keysToCamelCase(elem.Promo_Products_Models),
            promoEndDate: elem.promo_end_date,
            promoStartDate: elem.promo_start_date,
            percentFrom: elem.percent_from,
            loanPeriod: elem.loan_period || '',
            grace: elem.grace,
            loanAmount: String(elem.loan_amount),
            initialPaymentFrom: elem.initial_payment_from,
            initialPaymentTo: elem.initial_payment_to
          };

          return res;
        });
    }

    let additionalProducts: TAddProducts[] = [];
    if (additionalProductsDict && additionalProductsDict.length > 0) {
      const banks = dicts?.[DictTypes.Banks]?.getBy.name;
      const brand = productInfo.markModelYear?.brand;
      const dealer = dicts?.[DictTypes.Dealers]?.getBy.code[productInfo.dealer];

      const isDealerMultiBrand: boolean =
        !!dealer && dealer?._brandUuids.length > 1;
      const isChevroletOrKiaDealer: boolean =
        !isDealerMultiBrand &&
        [2, 43].some(
          (brandCode) =>
            dicts?.[DictTypes.Brands]?.getBy.uuid[
              safeKey(dealer?._brandUuids[0])
            ]?.code === brandCode
        );

      additionalProducts = additionalProductsDict
        .filter((item) => item.bank_name && banks?.[item.bank_name]?.is_active)
        // Первый мап, чтобы добавить fieldName
        .map((elem) => {
          return {
            id: elem.uuid,
            value: elem.bank,
            label: elem.name,
            checked: false,
            info: elem.description,
            code: elem.code,
            grace: elem.grace,
            bankName: elem.bank_name,
            selectedInsuranceUuid: null,
            insuranceUuid: null,
            price: elem.product_price,
            loanPeriod: '',
            partnerCode: elem.partner_code,
            productCode: elem.product_code,
            hidden:
              // Скрываем КАСКО у Береке по Chevrolet или Kia
              isBerekeKasko(elem.code)
                ? isDealerMultiBrand
                  ? ['2', '43'].some((brandCode) => brand?.code === brandCode)
                  : isChevroletOrKiaDealer
                : false,
            hiddenByAutoType: false,

            fieldName: RADIO_PRODUCTS_FIELD_NAMES[elem.code] // For radio button functionality
          } satisfies TAddProducts;
        })
        // Второй мап, чтобы обновить checked
        .map((item, index, array) => {
          const checkedProduct = orderApp.productInfo.additionalProducts?.find(
            (value) => value.code === item.code
          );
          const lastRadioGift = array.findLastIndex(
            (value) => value.fieldName === item.fieldName && value.price === 0
          );
          const checkedGiftProduct =
            item.price === 0 && orderApp.status === 'draft' && !item.fieldName;
          const checkedRadioGift = !!item.fieldName && index === lastRadioGift;

          return {
            ...item,
            loanPeriod: checkedProduct?.loan_period || '12',
            checked: !!checkedProduct || checkedGiftProduct || checkedRadioGift
          };
        });
    }

    const clientInfo: ClientInfoInterface = {
      birthDate: orderApp.personalInfo.dateBirth
        ? dayjs(orderApp.personalInfo.dateBirth).format('YYYY-MM-DD')
        : dayjs(getBirthDateFromIIN(orderApp.personalInfo.clientIin)).format(
            'YYYY-MM-DD'
          ) || '',
      documentType: String(orderApp.personalInfo.docType || ''),
      gender: String(orderApp.personalInfo.sex || ''),
      marriageStatus: String(orderApp.personalInfo.familyStatus || ''),
      childrenCount: orderApp.personalInfo.children
    };

    const clientJobInfo: ClientJobInfoInterface = {
      companyName: orderApp.personalInfo.companyName,
      companyType: orderApp.personalInfo.companyType,
      jobTitle: orderApp.personalInfo.jobTitle,
      jobAddress: orderApp.personalInfo.jobAddress,
      salary: formatPrice(orderApp.personalInfo.salary),
      additionalSalary: formatPrice(orderApp.personalInfo.additionalSalary),
      workPhoneNumber: formatPhoneNumber(orderApp.personalInfo.workTelNumber),
      socialStatus: String(orderApp.personalInfo.socialStatus || ''),
      isIndividualEntrepreneur: leasingOrder?.isClientIpOwner
    };

    const clientContacts: ClientContactsInterface = {
      contactName: orderApp.personalInfo.addContactsFullName,
      contactPhone: formatPhoneNumber(orderApp.personalInfo.addContactsTelNum),
      contactType: String(orderApp.personalInfo.addContactsIsRelated || '')
    };

    const documents: DocumentsInterface | object =
      orderApp.additionalInfo.files || {};

    // Запись данных в форму
    methods.reset({
      ...productInfo,
      eurasianTariffType: orderProduct.eurasianTariffType || 'STANDARD',
      stockBounds,
      ...clientInfo,
      ...clientJobInfo,
      ...clientContacts,
      ...documents,
      additionalProducts
    });
  }, [orderApp?.uuid]);

  useEffect(() => {
    const currentAddProducts = formData.additionalProducts;

    if (!orderApp || currentAddProducts?.length === 0) return;

    const brand = formData.markModelYear?.brand;
    const dealer =
      dicts?.[DictTypes.Dealers]?.getBy.code[safeKey(formData.dealer)];

    const isDealerMultiBrand: boolean =
      !!dealer && dealer?._brandUuids.length > 1;
    const isChevroletOrKiaDealer: boolean =
      !isDealerMultiBrand &&
      [2, 43].some(
        (brandCode) =>
          dicts?.[DictTypes.Brands]?.getBy.uuid[safeKey(dealer?._brandUuids[0])]
            ?.code === brandCode
      );

    // Скрываем КАСКО у Береке по Chevrolet или Kia
    if (
      isDealerMultiBrand
        ? ['2', '43'].some((brandCode) => brand?.code === brandCode)
        : isChevroletOrKiaDealer
    ) {
      methods.setValue(
        'additionalProducts',
        currentAddProducts?.map((item) => {
          const product = item as TAddProducts;
          return {
            ...product,
            hidden: isBerekeKasko(product.code)
              ? isFieldsFrozen
                ? !product.checked
                : true
              : false
          };
        }) ?? []
      );
    } else {
      // Восстановление скрытых продуктов
      methods.setValue(
        'additionalProducts',
        currentAddProducts?.map((item) => {
          const product = item as TAddProducts;
          return {
            ...product,
            hidden: false
          };
        }) ?? []
      );
    }
  }, [
    formData.dealer,
    formData.markModelYear?.brand?.code,
    dicts,
    isFieldsFrozen
  ]);

  useEffect(() => {
    return () => {
      methods.reset(defaultAutoCreditValues);
    };
  }, []);

  return methods;
};

export default useAutoCreditForm;
